import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import TemplatesArticles from "../../templates/articles";
import { COMMON_ROUTE } from "../../constants/routes";
import { WEB_TITLE } from "../../constants/config_ui";
import history from "../../routers/history";
import {
  trackEvent,
  trackEventWithTimeout,
} from "../../service/event-service";

const articleData = {
  author: "The Unicorn Club Research Team",
  title: "The 2026 Southeast Asia Startup Funding Database",
  date: "29 July 2026",
  longread: 6,
};

export default function Articles() {
  const location = useLocation();
  const [opening, setOpening] = useState(false);

  useEffect(() => {
    window.document.title = `${articleData.title} | ${WEB_TITLE}`;
    trackEvent("landing_viewed");
  }, []);

  const openDatabase = async () => {
    if (opening) return;

    setOpening(true);
    const result = await trackEventWithTimeout("cta_clicked");
    const trackingStatus = result.recorded
      ? "recorded"
      : result.reason === "timeout"
      ? "pending"
      : "failed";

    history.push({
      pathname: COMMON_ROUTE.training.path,
      search: location.search,
      state: { trackingStatus },
    });
  };

  return (
    <TemplatesArticles>
      <article className="article-data-container" id="article">
        <div className="article-kicker">2026 RESEARCH RELEASE</div>
        <div className="author-container">
          <span className="author">{articleData.author}</span>
          <span aria-hidden="true">·</span>
          <time dateTime="2026-07-29">{articleData.date}</time>
          <span aria-hidden="true">·</span>
          <span>{articleData.longread} min read</span>
        </div>

        <h1 className="premium-title">{articleData.title}</h1>
        <p className="article-lead">
          A practical view of publicly announced startup funding across
          Southeast Asia, organized for faster market mapping and research.
        </p>

        <div className="article-rule" aria-hidden="true" />

        <div className="article-body">
          <p>
            Funding information is often scattered across announcements,
            company updates, and investor portfolios. This research release
            brings the core details into one structured view so teams can
            compare activity across markets, stages, and sectors.
          </p>
          <p>
            Each entry is designed to make further research easier, with fields
            for the company, headquarters, sector, funding stage, announced
            investors, and source date.
          </p>

          <section
            className="database-preview"
            id="database-preview"
            aria-labelledby="database-preview-title"
          >
            <div className="database-preview-heading">
              <div>
                <span className="section-label">DATABASE PREVIEW</span>
                <h2 id="database-preview-title">Built for focused research</h2>
              </div>
              <span className="coverage-pill">Southeast Asia</span>
            </div>

            <ul className="database-fields">
              <li>
                <strong>Company &amp; market</strong>
                <span>Organization and headquarters</span>
              </li>
              <li>
                <strong>Round &amp; stage</strong>
                <span>Announced amount and funding stage</span>
              </li>
              <li>
                <strong>Sector</strong>
                <span>Comparable company categories</span>
              </li>
              <li>
                <strong>Investors</strong>
                <span>Reported participants in each round</span>
              </li>
            </ul>

            <div className="database-access">
              <div>
                <span className="access-eyebrow">FULL RESEARCH VIEW</span>
                <p>
                  Explore the complete set of tracked fields and source notes.
                </p>
              </div>
              <button
                type="button"
                className="database-cta"
                onClick={openDatabase}
                disabled={opening}
              >
                {opening ? "Opening securely…" : "Open the full database"}
                <span aria-hidden="true">→</span>
              </button>
            </div>
          </section>

          <section className="methodology" id="methodology">
            <span className="section-label">METHODOLOGY</span>
            <h2>How to use the database</h2>
            <p>
              Treat the database as a research starting point. Funding details
              can change after an announcement, so material decisions should
              always be checked against the linked primary source.
            </p>
          </section>
        </div>

        <aside className="author-info" aria-label="About the author">
          <span className="author-mark" aria-hidden="true">
            UC
          </span>
          <div>
            <span className="author-label">Written by</span>
            <strong>{articleData.author}</strong>
            <p>
              Tracking the companies, investors, and market shifts shaping
              Southeast Asia&apos;s startup ecosystem.
            </p>
          </div>
        </aside>
      </article>
    </TemplatesArticles>
  );
}
